/** Opens or closes a fenced code block. */
const CODE_FENCE = /^\s{0,3}(```|~~~)/;

/**
 * Where the code fence that is still open begins, or -1 when every block has closed.
 *
 * A block only closes on the marker that opened it, so the two fence characters are tracked apart:
 * counting them together makes a ``` block holding a ~~~ line look unclosed, and hides the rest of
 * the reply behind a block that has in fact already ended.
 */
const findOpenFence = (lines: string[]): number => lines.reduce((open, line, index) => {
  const marker = line.match(CODE_FENCE)?.[1];

  if (!marker) {
    return open;
  }

  if (open === -1) {
    return index;
  }

  return lines[open].match(CODE_FENCE)?.[1] === marker ? -1 : open;
}, -1);

/** Any row of a table, finished or not. */
const TABLE_ROW = /^\s*\|/;

/** The `|---|:--:|` row a table needs before its header counts as a table at all. */
const TABLE_SEPARATOR = /^\s*\|?[\s:|-]+\|?\s*$/;

/** A finished link or image target: `](url)`. */
const CLOSED_LINK = /\]\([^()\s]*\)/;

/** Inline runs that have to close before what they open is worth showing. Longest marker first. */
const INLINE_MARKERS = ['***', '**', '~~', '__', '*', '_', '`'];

/** The bullet or number that opens a list item. Its marker leads the line rather than emphasising. */
const LIST_MARKER = /^\s*(?:[-*+]|\d+[.)])\s+/;

/** Letters and digits, which an underscore between two of them belongs to rather than separates. */
const WORD_CHARACTER = /\w/;

/**
 * Whether an underscore sits inside a word, where markdown reads it as text.
 *
 * `snake_case` is one word to a reader and to every markdown parser, so treating its underscore as
 * an unclosed emphasis run would hide the rest of the line for as long as the stream took to
 * reach the next newline. Asterisks have no such rule: `a*b*c` really is emphasis.
 */
const isIntrawordUnderscore = (line: string, marker: string, at: number): boolean => (
  marker[0] === '_'
  && WORD_CHARACTER.test(line[at - 1] ?? '')
  && WORD_CHARACTER.test(line[at + marker.length] ?? '')
);

/**
 * Where the first inline run that never closes begins on one line, or -1 when the line is whole.
 *
 * Runs are matched per line, which is what the stream produces: a marker opened on one line and
 * closed on the next is rare enough to leave alone.
 */
const findUnclosedMarker = (line: string): number => {
  const open: { marker: string, index: number }[] = [];
  // A leading bullet is punctuation the line opens with, not a run it opens.
  let position = line.match(LIST_MARKER)?.[0].length ?? 0;

  while (position < line.length) {
    // Fixed for this pass, so the lookup below closes over a value rather than the cursor.
    const at = position;

    if (line[at] === '\\') {
      // An escaped character can neither open nor close a run.
      position = at + 2;
    } else {
      const marker = INLINE_MARKERS.find((candidate) => line.startsWith(candidate, at));

      if (!marker || isIntrawordUnderscore(line, marker, at)) {
        position = at + (marker?.length ?? 1);
      } else {
        const openedAt = open.findIndex((entry) => entry.marker === marker);

        if (openedAt === -1) {
          open.push({ marker, index: at });
        } else {
          open.splice(openedAt, 1);
        }

        position = at + marker.length;
      }
    }
  }

  return open.length ? open[0].index : -1;
};

/** Where a link or image that has not closed yet begins on one line, or -1. */
const findUnclosedLink = (line: string): number => {
  const opened = line.lastIndexOf('[');

  if (opened === -1) {
    return -1;
  }

  const rest = line.slice(opened);
  const closedText = rest.indexOf(']');

  // Only two shapes are half-written: text that has not closed its bracket, and a target that has
  // opened but not closed. A whole `[x]` on its own is a task-list box or a reference link, and
  // waiting for a `(` that is never coming would hide the line for good.
  const isOpen = closedText === -1
    || (rest.startsWith('](', closedText) && !CLOSED_LINK.test(rest));

  if (!isOpen) {
    return -1;
  }

  // The `!` of an image belongs to the part that waits.
  return opened > 0 && line[opened - 1] === '!' ? opened - 1 : opened;
};

/**
 * Bounds of the run of table rows the text ends on, as `[start, end)`.
 *
 * A text ending in a newline splits into a trailing empty line that sits after the table, so the
 * scan steps over it — otherwise a header whose newline has arrived is not seen as a table row at
 * all, and renders as literal pipes until its separator row lands.
 */
const findTableBounds = (lines: string[]): { start: number, end: number } => {
  const end = lines.length && lines[lines.length - 1] === '' ? lines.length - 1 : lines.length;
  let start = end;

  while (start > 0 && TABLE_ROW.test(lines[start - 1])) {
    start -= 1;
  }

  return { start, end };
};

/** Drops the trailing table rows that would render as a broken table. */
const takeCompleteTable = (lines: string[], isLastLineOpen: boolean): string[] => {
  const { start, end } = findTableBounds(lines);
  const rowCount = end - start;

  if (!rowCount) {
    return lines;
  }

  // Without its separator row a table is not a table yet, and the header would render as text.
  if (rowCount < 2 || !TABLE_SEPARATOR.test(lines[start + 1])) {
    return lines.slice(0, start);
  }

  // A row still being typed would render short, one cell at a time.
  return isLastLineOpen ? lines.slice(0, end - 1) : lines;
};

/**
 * Cuts a markdown string back to the part that is finished enough to render.
 *
 * Only for text that is still arriving. A reply streams one fragment at a time, so at almost every
 * frame the tail of it is half-written — an opened code fence with no closing one, a table header
 * with no rows under it yet, a `**` with nothing after it. Rendering that raw makes the message
 * flicker between markup and text as it grows, so the unfinished tail waits instead. Nothing is
 * lost: the next fragment either closes the run or extends what is already hidden.
 *
 * A finished message must not go through this. Its markers are whole, and any that are not were
 * meant literally.
 */
const takeCompleteMarkdown = (text: string): string => {
  if (!text) {
    return text;
  }

  const lines = text.split('\n');
  // A text ending in a newline splits into a final empty line, so the last line is only still
  // being written when that empty line is absent.
  const isLastLineOpen = lines[lines.length - 1] !== '';

  const openFence = findOpenFence(lines);
  const withoutOpenFence = openFence === -1 ? lines : lines.slice(0, openFence);

  // Only the line the text actually ends on can be half-written. Once an open fence has been cut
  // away, the line now last had its newline already and is whole.
  const isTailOpen = isLastLineOpen && withoutOpenFence.length === lines.length;
  const visible = takeCompleteTable(withoutOpenFence, isTailOpen);

  if (!visible.length) {
    return '';
  }

  // Anything already dropped above took the unfinished tail with it, so what is last now had its
  // newline and is whole. Only a line the text still ends on can hold a half-written run.
  if (visible.length !== lines.length || !isLastLineOpen) {
    return visible.join('\n');
  }

  const lastIndex = visible.length - 1;
  const lastLine = visible[lastIndex];
  const cuts = [findUnclosedMarker(lastLine), findUnclosedLink(lastLine)].filter((cut) => cut >= 0);

  if (!cuts.length) {
    return visible.join('\n');
  }

  return [...visible.slice(0, lastIndex), lastLine.slice(0, Math.min(...cuts)).trimEnd()].join('\n');
};

export default takeCompleteMarkdown;
